import { Tabs } from '@mui/material'
import { RobotTypeEnum } from 'api/schema'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import Loader from 'components/Loader/Loader'
import RobotTypeLabel from 'components/RobotTypeLabel/RobotTypeLabel'
import { StyledTab } from 'components/StyledTab/StyledTab'
import TabPanel from 'components/TabPanel/TabPanel'
import Title from 'components/Title/Title'
import ViewZonesMode from 'pages/Mission/MissionView/Modes/ViewZone/ViewZonesMode'
import RobotControl from 'pages/Robot/RobotView/RobotLeftBlock/RobotControl/RobotControl'
import RobotInfo from 'pages/Robot/RobotView/RobotLeftBlock/RobotDetails/RobotInfo/RobotInfo'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { MissionControlActions } from 'store/actions/missionControl'
import { selectMission } from 'store/selectors/mission'
import { selectLoadingProgress } from 'store/selectors/missionControl'
import { selectRobot } from 'store/selectors/robot'
import store from 'store/store'
import { missionControlThunks } from 'store/thunks/missionControl'
import { robotThunks } from 'store/thunks/robot'
import { PROGRESS_TYPES, TIMEOUT_FETCHING_STATUS } from 'utils/constants'
import { PATH } from 'utils/constants'
import { isRobotNotFoundError } from 'utils/robotFetchErrors'
import { VENDOR } from 'utils/vendorDisplayName'

import MissionControlMap from './MissionControlMap/MissionControlMap'
import MissionStats from './MissionStats/MissionStats'
import RobotControlButtons from './RobotControlButtons/RobotControlButtons'

import style from './MissionControl.module.css'
import pageLayoutStyle from 'styles/pageLayout.module.css'

const MissionControl = () => {
  const { t } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()
  const robotId = parseFloat(params.id || '')
  const mission = useSelector(selectMission)
  const robot = useSelector(selectRobot)
  const [tab, setTab] = useState(0)
  const loadingProgress = useSelector(selectLoadingProgress)
  const hideCleaningType = mission.missionInfo ? mission.missionInfo.robot_type !== RobotTypeEnum.COMBO : false
  const [missionId, setMissionId] = useState(0)
  const [initialized, setInitialized] = useState(false)
  const isInitialized = React.useRef(false)
  const isInitializing = React.useRef(false)
  const currentRobotId = React.useRef<number>(robotId)

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue)
  }

  // Initial data loading and handling robotId changes
  useEffect(() => {
    // If robotId changed, reset initialization flags
    if (currentRobotId.current !== robotId) {
      isInitialized.current = false
      isInitializing.current = false
      currentRobotId.current = robotId
      setMissionId(0)
      setInitialized(false)
    }

    // Initialize only if not yet done and not in progress
    if (!isInitialized.current && !isInitializing.current) {
      isInitializing.current = true
      const initialize = async () => {
        try {
          if (!Number.isFinite(robotId) || robotId < 1) {
            navigate(PATH.ASSET_LIST, { replace: true })
            return
          }
          // Fetch robot first
          await store.dispatch(robotThunks.fetchRobot(robotId))
          const mission_id = selectRobot(store.getState()).robotInfo?.job?.missionId || 0

          // Only fetch mission if we have a mission_id and it's different from current missionId
          const currentMissionId = selectMission(store.getState()).missionInfo?.id
          const currentLoadingProgress = selectLoadingProgress(store.getState())

          // Don't fetch if already loading or if mission is already loaded
          if (mission_id && mission_id !== currentMissionId && currentLoadingProgress !== PROGRESS_TYPES.WORK) {
            // Never show loader on initial load - data will appear when ready
            // This prevents the "double loading" effect
            await store.dispatch(missionControlThunks.fetchMission(mission_id, false))
            setMissionId(mission_id)
          } else if (mission_id) {
            // Mission already loaded, just set the missionId state
            setMissionId(mission_id)
          }

          // Set flags only after data loading is complete (setInitialized triggers re-render so polling effect runs)
          isInitialized.current = true
          setInitialized(true)
        } catch (error) {
          // On error, reset flag to allow retry
          isInitialized.current = false
          setInitialized(false)
          if (isRobotNotFoundError(error)) {
            navigate(PATH.ASSET_LIST, { replace: true })
          }
        } finally {
          isInitializing.current = false
        }
      }
      initialize()
    }
  }, [robotId, navigate])

  useEffect(() => {
    // Don't start interval until initialization is complete
    if (!isInitialized.current || !robot.robotInfo) {
      return
    }

    const interval = setInterval(async () => {
      if (robot.robotInfo) {
        try {
          // Silent update: do not set loadingProgress so content stays mounted and interval is not cleared
          await store.dispatch(robotThunks.fetchRobotSilent(robot.robotInfo.id))
          const updatedRobot = selectRobot(store.getState())
          const newMissionId = updatedRobot.robotInfo?.job?.missionId
          const currentLoadingProgress = selectLoadingProgress(store.getState())

          // Only fetch mission if missionId actually changed, we have a valid missionId, and not already loading
          if (
            newMissionId &&
            missionId !== newMissionId &&
            newMissionId > 0 &&
            currentLoadingProgress !== PROGRESS_TYPES.WORK
          ) {
            setMissionId(newMissionId)
            // Don't show loader when updating mission in interval (silent update)
            await store.dispatch(missionControlThunks.fetchMission(newMissionId, false))
          }
        } catch (error) {
          // Silently handle network errors (ERR_NETWORK_CHANGED, Failed to fetch, etc.)
          // These are common when network connection is unstable and don't need user attention
          if (error instanceof TypeError && error.message === 'Failed to fetch') {
            // Network error - silently ignore, will retry on next interval
            return
          }
          console.error('[MissionControl] Error updating robot data:', error)
        }
      }
    }, TIMEOUT_FETCHING_STATUS)
    return () => {
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robot.robotInfo?.id, missionId, initialized])

  // Reset loadingProgress on unmount to prevent stale state
  React.useEffect(() => {
    return () => {
      store.dispatch(MissionControlActions.setLoadingProgress(PROGRESS_TYPES.IDLE))
    }
  }, [])

  return (
    <div className={clsx(style.container, pageLayoutStyle.contentTopPadding)}>
      {loadingProgress === PROGRESS_TYPES.WORK ? (
        <Loader />
      ) : (
        <>
          <div className={pageLayoutStyle.pageHeaderArea}>
            <div className={clsx(style.titleBlock, pageLayoutStyle.titleBlockMargin)}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Title text={mission.missionInfo?.name || ''} />
                <div className={clsx('text-16-300', style.titleId)}>
                  {t('missions.id')}: {mission.missionInfo?.id ?? '—'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'flex-start' }}>
              <RobotTypeLabel vendor={VENDOR.AUTONOMICS} robotType={mission.missionInfo?.robot_type} />
            </div>
          </div>
          <div className={style.main}>
            <div className={style.leftBlock}>
              <Box className={style.box}>
                <Tabs value={tab} onChange={handleChange} indicatorColor="primary" sx={{ height: 52, minHeight: 52 }}>
                  <StyledTab
                    label={t('robot.details')}
                    sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0, width: 185 }}
                    disableRipple
                  />
                  <StyledTab
                    label={t('robot.control')}
                    sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0, width: 185 }}
                    disableRipple
                  />
                  <StyledTab
                    label={t('mission.details')}
                    sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0, width: 185 }}
                    disableRipple
                  />
                </Tabs>
                <TabPanel value={tab} index={0} disablePadding={true}>
                  <div className={style.mcTabTitleBlock}>
                    <Link
                      to={`${PATH.ROBOT}${robot.robotInfo?.id}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Title text={robot.robotInfo?.name || ''} />
                    </Link>
                  </div>
                  <div className={style.mcTabButtonsGap}>
                    <RobotControlButtons />
                  </div>
                  <MissionStats robot={robot.robotInfo} />
                  <RobotInfo />
                </TabPanel>
                <TabPanel value={tab} index={1} disablePadding={true}>
                  <RobotControl />
                </TabPanel>
                <TabPanel value={tab} index={2} disablePadding={true}>
                  <ViewZonesMode
                    cleaningZones={mission.cleaningZones}
                    restrictZones={mission.restrictZones}
                    hideActions={true}
                    hideCleaningType={hideCleaningType}
                  />
                </TabPanel>
              </Box>
            </div>
            <div className={style.map}>
              <MissionControlMap />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
export default MissionControl
